import React, { useEffect, useState } from 'react';
import { Routes, useNavigate, useParams, useLocation } from 'react-router-dom';
import moment from 'moment';
import Compressor from 'compressorjs';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import ProtectedRoute from '../../components/helpers/ProtectedRoute';
import Table from '../../components/DataTable/Table_old';
import Button from '../../components/Button';
import {
	AttributeRow,
	TemplateDate,
	TemplateDropdown,
	TemplateField,
	TemplateTextarea,
} from '../../components/Attributes/Attributes_old';
import {
	classNames,
	getStandardAssetName,
	getUserFullName,
	getArrayPosition,
} from '../../components/helpers/functions';
import { AvatarGenerated } from '../../components/Avatar';
// TODO: Update to use AssetSelectorBeta with assets data fetching
// import SelectAsset from '../../components/SelectAsset';
import SelectLocation from '../../components/SelectLocation';
import Badge from '../../components/Badge';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

import { addTicket, getTickets, getTicketsWithStats } from '../../api';
import { exportTickets } from '../../api/export';

const dataToCsv = (data) => {
	const url = window.URL.createObjectURL(new Blob([data]));
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute(
		'download',
		`Tickets-${moment().format('MM[/]DD[/]YYYY')}.csv`,
	);
	document.body.appendChild(link);
	link.click();
};

const generateConsumableDescription = (ticket) => {
	if (ticket?.consumableUrgency === 'immediately') {
		return `Need Immediately`;
	} else if (ticket?.consumableUrgency === 'low') {
		return `Running Low`;
	} else if (ticket?.consumableUrgency === 'date' && ticket?.consumable_date) {
		return `Need by: ${moment(ticket?.consumable_date).format('MMM Do, YYYY')}`;
	}
};

const PAGE_SIZE = 10;

export const TicketsList = () => {
	const navigate = useNavigate();
	const { open, setOpen: openOverview } = useModal('ticketOverview');
	const { open: createOpen, setOpen: openCreateTicket } = useModal('createTicket');

	const { id } = useParams();
	console.log('id: ', id);
	const location = useLocation();

	const { value: csvData, execute: onExportTickets } = useAsync(exportTickets, {
		immediate: false,
	});

	console.log('locatiom: ', location);

	let tabParam = getArrayPosition(
		location?.pathname?.split('/').filter(Boolean) || [],
		3,
	);

	const activeTab = [
		'all',
		'incoming',
		'in-progress',
		'open',
		'closed',
		'create',
	].includes(tabParam)
		? tabParam
		: undefined;

	const [filters, setFilters] = useState({
		...(activeTab
			? {
					status:
						activeTab === 'in-progress'
							? 'inprogress'
							: activeTab === 'all'
								? ['incoming', 'inprogress', 'done', 'closed']
								: activeTab,
				}
			: { status: ['incoming', 'inprogress'] }),
	});
	const [page, setPage] = useState(1);

	const { value: tickets, execute: refreshTickets } = useAsync(getTickets, {
		immediate: false,
	});
	const { value: stats, execute: refreshTicketStats } =
		useAsync(getTicketsWithStats);

	useEffect(() => {
		refreshTickets(
			{
				...filters,
				page,
				pageSize: PAGE_SIZE,
			},
			null,
			{
				overrideDefault: true,
			},
		);
	}, [activeTab, page]);

	useEffect(() => {
		if (
			id &&
			id !== 'status' &&
			id !== 'incoming' &&
			id !== 'in-progress' &&
			id !== 'all' &&
			id !== 'closed'
		) {
			openOverview({
				id,
				onClose: (path) => {
					navigate(path || '/tickets');
				},
			});
		}
	}, [id]);

	// Handle /tickets/create route - open modal
	useEffect(() => {
		if (activeTab === 'create' && !createOpen) {
			openCreateTicket({
				onSuccess: () => {
					refreshTicketStats({}, null, { silent: true });
					refreshTickets(
						{
							...filters,
							page,
							pageSize: PAGE_SIZE,
						},
						null,
						{
							overrideDefault: true,
							silent: true,
						},
					);
					navigate('/tickets');
				},
			});
		}
	}, [activeTab]);


	useEffect(() => {
		if (!open) {
			refreshTicketStats({}, null, { silent: true });
			refreshTickets(
				{
					...filters,
					page,
					pageSize: PAGE_SIZE,
				},
				null,
				{
					overrideDefault: true,
					silent: true,
				},
			);
		}
	}, [open]);

	let hasPrev = false;
	let hasNext = false;

	const total = tickets?.absoluteTotal;

	const start = total ? PAGE_SIZE * page - (PAGE_SIZE - 1) : 0;
	let end = PAGE_SIZE * page;

	if (end < total) {
		hasNext = true;
	} else {
		end = total;
	}

	if (start > PAGE_SIZE) {
		hasPrev = true;
	}

	return (
		<MainContent>
			<PageHeading
				actions={[
					{
						name: 'Report an Issue',
						onClick: () => openCreateTicket({
							onSuccess: () => {
								refreshTicketStats({}, null, { silent: true });
								refreshTickets(
									{
										...filters,
										page,
										pageSize: PAGE_SIZE,
									},
									null,
									{
										overrideDefault: true,
										silent: true,
									},
								);
							},
						}),
					},
				]}
				divider={false}
			>
				Tickets
			</PageHeading>

			<div className="border-b border-gray-200 mb-4">
				<nav className="-mb-px flex space-x-8" aria-label="Tabs">
					{[
						{
							name: 'Active',
							key: 'active',
							current: !activeTab,
							count: stats?.incoming + stats?.inprogress,
							onClick: () => {
								setFilters({
									...filters,
									...{ status: ['incoming', 'inprogress'] },
								});
							},
						},
						{
							name: 'Incoming',
							key: 'incoming',
							current: activeTab === 'incoming',
							onClick: () =>
								setFilters({ ...filters, ...{ status: 'incoming' } }),
							count: stats?.incoming,
						},
						{
							name: 'In Progress',
							key: 'in-progress',
							current: activeTab === 'in-progress',
							onClick: () =>
								setFilters({ ...filters, ...{ status: 'inprogress' } }),
							count: stats?.inprogress,
						},
						{
							name: 'Closed',
							key: 'closed',
							current: activeTab === 'closed',
							onClick: () =>
								setFilters({ ...filters, ...{ status: 'closed' } }),
							count: stats?.closed,
						},
						{
							name: 'All',
							key: 'all',
							current: activeTab === 'all',
							count: stats?.total,
							onClick: () => {
								setFilters({});
							},
						},
					].map((tab) => (
						<div
							key={tab.name}
							onClick={() => {
								navigate(`/tickets/status/${tab.key}`);
								if (typeof tab.onClick === 'function') {
									tab.onClick();
								}
							}}
							className={classNames(
								tab.current
									? 'border-indigo-500 text-indigo-600'
									: 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700',
								'flex whitespace-nowrap border-b-2 py-4 px-1 text-xs font-medium cursor-pointer items-center',
							)}
							aria-current={tab.current ? 'page' : undefined}
						>
							{tab.name}
							{tab.count ? (
								<span
									className={classNames(
										tab.current
											? 'bg-indigo-100 text-indigo-600'
											: 'bg-gray-100 text-gray-900',
										'ml-3 hidden rounded-full py-0.5 px-2.5 text-xxs font-medium md:inline-block',
									)}
								>
									{tab.count}
								</span>
							) : null}
						</div>
					))}
				</nav>
			</div>

			<Table
				columns={[
					{
						dataIndex: 'id',
						render: (id, row) => {
							return (
								<div className="flex items-center text-gray-800">
									<div>
										<div>
											<span className="font-semibold">
												{row?.ticketCode ? `${row?.ticketCode}: ` : ''}
											</span>
											{row?.subject ? `${row?.subject}` : ''}
											{row?.type === 'consumable' && `: ${row?.consumable}`}
										</div>
										<div className="text-xxs text-gray-500 font-normal">
											{row?.type === 'asset'
												? getStandardAssetName(row?.asset || {})
												: null}
											{(row?.type === 'general' || row?.type === 'incident') &&
											row?.location
												? row?.location?.name
												: null}
											{row?.type === 'consumable'
												? generateConsumableDescription(row)
												: null}
										</div>
									</div>
								</div>
							);
						},
					},
					/*{
						title: 'Status',
						dataIndex: 'status',
						render: (status) => (
							<Badge
								status={
									{
										incoming: 'error',
										inprogress: 'warning',
										done: 'success',
										closed: 'default',
									}[status]
								}
								hasDot={false}
								//ghost
								size={'xxs'}
							>
								{
									{
										incoming: 'Incoming',
										inprogress: 'In Progress',
										done: 'Done',
										closed: 'Closed',
									}[status]
								}
							</Badge>
						),
					},*/
					{
						title: 'Status',
						dataIndex: 'status',
						render: (status) => {
							if (status === 'done') {
								return (
									<Badge
										status={'success'}
										hasDot={false}
										size={'xxs'}
										isBeta
										ghost={closed}
									>
										Done
									</Badge>
								);
							}
							if (status === 'closed') {
								return (
									<Badge hasDot={false} size={'xxs'} isBeta ghost={closed}>
										Closed
									</Badge>
								);
							}
							return {
								incoming: 'Incoming',
								inprogress: 'In Progress',
								done: 'Done',
								closed: 'Closed',
							}[status];
						},
					},
					{
						title: 'Severity',
						dataIndex: 'severity',
						render: (severity, ticket) => {
							const closed =
								ticket.status === 'closed' || ticket?.status === 'done';
							return (
								<Badge
									status={
										closed
											? 'default'
											: {
													critical: 'error',
													major: 'warning',
													minor: 'caution',
												}[severity]
									}
									hasDot={false}
									size={'xxs'}
									isBeta
									ghost={closed}
								>
									{
										{
											critical: (
												<div className="flex">
													{!closed ? (
														<ExclamationTriangleIcon className="w-4 h-4 mr-1" />
													) : null}
													Critical
												</div>
											),
											major: 'Major',
											minor: 'Minor',
										}[severity]
									}
								</Badge>
							);
						},
					},
					{
						title: 'Lead',
						dataIndex: 'assignedTo',
						render: (assignedTo) => (
							<div>
								{assignedTo ? (
									<div className="flex items-center">
										<AvatarGenerated
											id={getUserFullName(assignedTo)}
											size={'xxs'}
											className="mr-2"
										/>
										{getUserFullName(assignedTo)}
									</div>
								) : (
									<>Unassigned</>
								)}
							</div>
						),
					},
					{
						title: 'Elapsed Time',
						dataIndex: 'createdAt',
						render: (createdAt, row) => (
							<div>
								{row?.status === 'closed' || row?.status === 'done'
									? moment
											.duration(
												moment(row.createdAt).diff(
													moment(row?.closedAt || row?.updatedAt),
												),
											)
											.humanize()
									: moment(createdAt).fromNow(true)}
							</div>
						),
					},
				]}
				hasCheckboxes={false}
				exportable={false}
				onExport={(selected) =>
					onExportTickets(
						{
							selected,
							columns: [
								'id',
								'type',
								'status',
								'subject',
								'description',
								'severity',
							],
							filters: {
								status: activeTab,
							},
						},
						(response) => {
							dataToCsv(response);
						},
					)
				}
				dataSource={tickets?.rows}
				emptyTableText={'Hurray!  No tickets are found'}
				onRow={(row) => navigate(`/tickets/${row.id}`)}
				trClassName={(row, index) => {
					const colorVariants = {
						critical: 'border-l-4 border-red-200',
						major: 'border-l-4 border-orange-200',
						minor: 'border-l-4 border-yellow-200/80',
					};
					return row?.status === 'closed' || row?.status === 'done'
						? 'border-l-4'
						: colorVariants[row?.severity || 'minor'];
				}}
			/>

			{total ? (
				<div className="flex justify-end">
					<div className="flex text-gray-600 text-xs mt-6">
						{`Viewing ${start} - ${end} of ${total} Total`}
						<div>
							{hasPrev && (
								<span
									className="ml-4 text-indigo-500 cursor-pointer"
									onClick={() => setPage(page - 1)}
								>
									Prev
								</span>
							)}
							{hasNext && (
								<span
									className="ml-4 text-indigo-500 cursor-pointer"
									onClick={() => setPage(page + 1)}
								>
									Next
								</span>
							)}
						</div>
					</div>
				</div>
			) : null}
		</MainContent>
	);
};

const activity = [
	{
		id: 1,
		type: 'comment',
		person: { name: 'Eduardo Benz', href: '#' },
		imageUrl:
			'https://images.unsplash.com/photo-1520785643438-5bf77931f493?ixlib=rb-=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=8&w=256&h=256&q=80',
		comment:
			'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Tincidunt nunc ipsum tempor purus vitae id. Morbi in vestibulum nec varius. Et diam cursus quis sed purus nam. ',
		date: '6d ago',
	},
	{
		id: 2,
		type: 'assignment',
		person: { name: 'Hilary Mahy', href: '#' },
		assigned: { name: 'Kristin Watson', href: '#' },
		date: '2d ago',
	},
	{
		id: 3,
		type: 'tags',
		person: { name: 'Hilary Mahy', href: '#' },
		tags: [
			{ name: 'Bug', href: '#', color: 'bg-rose-500' },
			{ name: 'Accessibility', href: '#', color: 'bg-indigo-500' },
		],
		date: '6h ago',
	},
	{
		id: 4,
		type: 'comment',
		person: { name: 'Jason Meyers', href: '#' },
		imageUrl:
			'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?ixlib=rb-=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=8&w=256&h=256&q=80',
		comment:
			'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Tincidunt nunc ipsum tempor purus vitae id. Morbi in vestibulum nec varius. Et diam cursus quis sed purus nam. Scelerisque amet elit non sit ut tincidunt condimentum. Nisl ultrices eu venenatis diam.',
		date: '2h ago',
	},
];

export const NewTicket = () => {
	const navigate = useNavigate();
	const urlSearchParams = new URLSearchParams(window.location.search);
	const params = Object.fromEntries(urlSearchParams.entries());

	const [isUploading, setIsUploading] = useState(false);

	const [form, setForm] = useState({
		assetId: params?.assetId,
		type: params?.assetId ? 'asset' : null,
	});
	const setItem = (key, value) => setForm({ ...form, [key]: value });

	const { execute: onAdd, status } = useAsync(addTicket, { immediate: false });
	const [fileList, setFileList] = useState([]);

	const hasAllRequiredValues = [
		'type',
		...(form?.type === 'asset' ? ['assetId'] : []),
		...(form?.type === 'consumable' ? ['consumable'] : []),
	].every((key) =>
		Object.keys(form)
			.filter((key) => form[key])
			.includes(key),
	);

	return (
		<MainContent
			sidebarRight={
				<>
					<div className="text-gray-500 text-sm">Types</div>

					{[
						{
							title: 'General',
							description:
								'The general ticket type is anything not covered by the below ticket types',
						},
						{
							title: 'Assets',
							description:
								'Having issues with a specific asset?  Selecting this ticket type will attach this issue to the selected asset and keep it in its records',
						},
						{
							title: 'Consumables',
							description:
								'Missing or running low on a consumble?  This ticket type notifies your lab ops team to get this ordered as soon as possible',
						},
						{
							title: 'Incident',
							description:
								'Reporting a spill or general lab accidents?  This ticket type has an option to notify the lab ops team with an emergency alert for them to respond right away',
						},
					].map(({ title, description }) => (
						<div className="mt-4 mb-2">
							<div className="text-sm font-medium text-gray-600">{title}</div>
							<p className="text-gray-400 text-xs leading-5">{description}</p>
						</div>
					))}
				</>
			}
		>
			<PageHeading breadcrumbs={[{ name: 'Tickets', path: '/tickets' }]}>
				Report an Issue
			</PageHeading>
			<main className="mx-auto max-w-lg px-4 pt-10 pb-12 lg:pb-16">
				<form>
					<div className="space-y-6">
						<TemplateDropdown
							name="Type"
							options={[
								{ label: 'General', value: 'general' },
								{ label: 'Asset', value: 'asset' },
								{ label: 'Consumable', value: 'consumable' },
								{ label: 'Incident', value: 'incident' },
							]}
							placeholder="What type of issue are you reporting?"
							dataIndex="type"
							onChange={(key, value) => setItem(key, value)}
							value={form?.type}
						/>

						{form?.type === 'asset' && (
							<>
								<AttributeRow
									name="Select an asset"
									isEditing={true}
									required={true}
								>
									{/* TODO: Replace with AssetSelectorBeta */}
									<div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-sm">
										<p className="text-yellow-800">
											Asset selection disabled - needs AssetSelectorBeta integration
										</p>
									</div>
								</AttributeRow>

								<div className="w-full border-t border-gray-300 my-6"></div>
							</>
						)}

						{form?.type === 'consumable' && (
							<>
								<TemplateField
									name="What consumable?"
									placeholder="Please describe in detail"
									dataIndex="consumable"
									value={form?.consumable}
									onChange={(key, value) => setItem(key, value)}
									required
								/>

								<TemplateDropdown
									name="Urgency"
									placeholder="How urgently do you need this?"
									dataIndex="consumableUrgency"
									options={[
										{ label: 'Need Immediately', value: 'immediately' },
										{ label: 'Running low', value: 'low' },
										{ label: 'By a specific date', value: 'date' },
									]}
									value={form?.consumableUrgency || 'low'}
									onChange={(key, value) => setItem(key, value)}
								/>

								{form?.consumableUrgency === 'date' && (
									<TemplateDate
										name="By this date"
										dataIndex="consumableDate"
										onChange={(key, value) => setItem(key, value)}
										value={form?.consumableDate}
									/>
								)}
							</>
						)}

						{form?.type && form?.type !== 'consumable' && (
							<>
								<TemplateField
									name="Brief summary"
									placeholder="Enter brief summary"
									dataIndex="summary"
									value={form?.summary}
									onChange={(key, value) => setItem(key, value)}
									optional
									maxLength={50}
									showMaxLength
								/>

								<TemplateTextarea
									name="Description"
									dataIndex="description"
									onChange={(key, value) => {
										setItem(key, value);
									}}
									value={form?.description}
								/>

								<TemplateDropdown
									name="Severity"
									options={[
										{ label: 'Critical', value: 'critical' },
										{ label: 'Major', value: 'major' },
										{ label: 'Minor', value: 'minor' },
									]}
									placeholder="What is the level of urgency?"
									dataIndex="severity"
									onChange={(key, value) => setItem(key, value)}
									value={form?.severity}
								/>
							</>
						)}

						{form?.type === 'asset' && (
							<div>
								<label
									htmlFor="description"
									className="block text-sm font-medium text-gray-700"
								>
									Photos
								</label>
								<div className="mt-1">
									<div className="mt-1 sm:col-span-2 sm:mt-0">
										<div className="flex max-w-lg justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6">
											<div className="space-y-1 text-center">
												<svg
													className="mx-auto h-12 w-12 text-gray-400"
													stroke="currentColor"
													fill="none"
													viewBox="0 0 48 48"
													aria-hidden="true"
												>
													<path
														d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
														strokeWidth={2}
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
												<div className="text-sm text-gray-600">
													<label
														htmlFor="file-upload"
														className="relative cursor-pointer rounded-md bg-white font-medium text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:text-indigo-500"
													>
														<span>Upload a photo</span>
														<input
															accept="image/jpeg, image/png"
															id="file-upload"
															name="file-upload"
															type="file"
															className="sr-only"
															onChange={(e) => {
																setIsUploading(true);

																const reader = new FileReader();
																const imageFile = e.target.files[0];

																new Compressor(imageFile, {
																	quality: 0.2,
																	success: (compressedResult) => {
																		reader.readAsDataURL(compressedResult);
																	},
																});

																reader.onloadend = (result) => {
																	setIsUploading(false);
																	setFileList([
																		...fileList,
																		{
																			name: imageFile.name,
																			original: imageFile,
																			size: result.total,
																			thumbUrl: reader.result,
																		},
																	]);
																};
															}}
														/>
													</label>
												</div>
												<p className="text-xs text-gray-500">
													PNG, JPG, up to 10MB
												</p>
											</div>
										</div>
									</div>

									<ul
										role="list"
										className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 xl:gap-x-8 my-6"
									>
										{fileList?.map((file, i) => {
											return (
												<li
													key={`photo-${i}`}
													className="relative flex flex-col items-center justify-center"
												>
													<div className="group aspect-w-10 aspect-h-7 block w-full overflow-hidden rounded-lg bg-gray-100">
														<img
															alt=""
															className="pointer-events-none object-contain"
															src={file.thumbUrl}
														/>
													</div>
													<p className="pointer-events-none mt-2 block truncate text-xs font-medium text-gray-900">
														{file.name}
													</p>
													<p className="pointer-events-none block text-xxs font-medium text-gray-500 mt-2">
														{`${(file.size / (1024 * 1024)).toFixed(2)} MB`}
													</p>
												</li>
											);
										})}
									</ul>

									{isUploading && (
										<div className="relative flex flex-col items-center justify-center">
											<div role="status">
												<svg
													aria-hidden="true"
													class="w-5 h-5 mr-2 text-gray-200 animate-spin dark:text-gray-200 fill-blue-600"
													viewBox="0 0 100 101"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
														fill="currentColor"
													/>
													<path
														d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
														fill="currentFill"
													/>
												</svg>
												<span class="sr-only">Loading...</span>
											</div>
										</div>
									)}
								</div>
							</div>
						)}

						{(form?.type === 'general' || form?.type === 'incident') && (
							<AttributeRow name="Location">
								<SelectLocation
									dataIndex="location"
									value={form?.location}
									onChange={(id) => setItem('location', id)}
								/>
							</AttributeRow>
						)}

						<div className="flex justify-end">
							<Button type="secondary" onClick={() => navigate('/tickets')}>
								Cancel
							</Button>
							<Button
								disabled={!hasAllRequiredValues}
								onClick={() =>
									onAdd(configurePayload({ ...form, fileList }), () =>
										navigate('/tickets'),
									)
								}
							>
								{status === 'pending'
									? fileList?.length
										? 'Uploading photos...'
										: 'Creating...'
									: 'Create'}
							</Button>
						</div>
					</div>
				</form>
			</main>
		</MainContent>
	);
};

const configurePayload = ({
	assetId,
	summary,
	description,
	fileList,
	location,
	operational,
	consumable,
	consumableUrgency,
	consumableDate,
	incidentActive,
	type,
	severity,
}) => {
	return {
		information: {
			...(assetId ? { assetId } : {}),
			...(summary ? { summary } : {}),
			...(description ? { description } : {}),
			...(location ? { location } : {}),
			...(operational ? { operational } : {}),
			...(consumable ? { consumable } : {}),
			...(consumableUrgency ? { consumableUrgency } : {}),
			...(consumableDate ? { consumableDate } : {}),
			...(incidentActive ? { incidentActive } : {}),
			...(type ? { type } : {}),
			...(severity ? { severity } : {}),
		},
		files: fileList?.map(({ original }) => original),
		bucket: 'tickets',
	};
};

export default () => (
	<Routes>
		<ProtectedRoute exact path="/tickets" component={TicketsList} />
		<ProtectedRoute exact path="/tickets/incoming" component={TicketsList} />
		<ProtectedRoute exact path="/tickets/open" component={TicketsList} />
		<ProtectedRoute exact path="/tickets/in-progress" component={TicketsList} />
		<ProtectedRoute exact path="/tickets/closed" component={TicketsList} />
		<ProtectedRoute exact path="/tickets/all" component={TicketsList} />
		<ProtectedRoute exact path="/tickets/create" component={NewTicket} />
		<ProtectedRoute exact path="/tickets/:id" component={TicketsList} />
	</Routes>
);
